import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-100 cursor-pointer border-solid font-sh5 font-normal",
  {
    variants: {
      variant: {
        default: "",
        destructive: "bg-red-500 text-gray-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-gray-50 dark:hover:bg-red-900/90",
        outline: "border border-gray-20 bg-white hover:bg-gray-100 hover:text-white dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50 bg-transparent",
        secondary: "bg-gray-10 text-gray-100",
        ghost: "",
        link: "text-[#1D1D1D] underline-offset-4 hover:underline bg-transparent",
        none: "border border-gray-20 bg-white hover:text-none dark:border-gray-800 bg-transparent",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", asChild = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const defaultVariantClass =
      variant === "default"
        ? disabled
          ? "bg-[#CFCFCF] text-white disabled:text-gray-60 dark:text-gray-900"
          : "bg-[#303030] text-white hover:bg-gray-900/90 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 disabled:bg-gray-60 disabled:text-gray-20"
        : "";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          defaultVariantClass,
          className
        )}
        ref={ref}
        type="button"
        disabled={disabled}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
